import React, { useEffect, useState } from "react";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import CommonPagination from "../../components/widgets/common_pagination";
import { CommonTextField } from "../../components/widgets/common_textField";
import AuthService from "../../service/auth.service";
import { toastError, toastSuccess } from "../../common/toastNotification";
import moment from "moment";
import {
  Search,
  Download,
  ExternalLink,
  Watch,
  CheckCircle,
  AlertTriangle,
  Award,
  Layers,
  Copy,
  Check,
  RefreshCw,
  FileText,
  User,
  Activity,
  Calendar,
  Grid3X3,
  X
} from "lucide-react";



// Helper fallback image component to prevent broken image icons
const WatchImage = ({ src, alt, className }) => {
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
  }, [src]);

  if (!src || error) {
    return (
      <div className={`bg-slate-100 flex flex-col items-center justify-center border border-slate-200 text-slate-400 p-4 ${className}`}>
        <Watch className="w-8 h-8 mb-1 opacity-40 animate-pulse" />
        <span className="text-[9px] font-semibold tracking-wider uppercase opacity-50">No Image</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setError(true)}
    />
  );
};

const Report = () => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [search, setSearch] = useState("");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  // Inspector Modal states
  const [selectedReport, setSelectedReport] = useState(null);
  const [isInspectorOpen, setIsInspectorOpen] = useState(false);
  const [activeImageKey, setActiveImageKey] = useState("frontImage");

  const handleDataSize = (value) => {
    setSize(value);
    setPage(1);
  };

  const getReportsList = async () => {
    setLoading(true);
    try {
      const response = await AuthService.getReports(page, size , search);
      setData(response?.data?.apiresponse?.data);
    } catch (error) {
      toastError(error?.response?.data?.message || "Failed to fetch reports from API");
      setData({});
    } finally {
      setLoading(false);
    }
  };

  // Fetch data with debounce for search and pagination
  useEffect(() => {
    const handler = setTimeout(() => {
      getReportsList();
    }, 300);
    return () => {
      clearTimeout(handler);
    };
  }, [page, size, search]);

  const handleCopyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toastSuccess(`${label} copied to clipboard!`);
  };

  const openInspector = (report) => {
    setSelectedReport(report);
    setActiveImageKey("frontImage");
    setIsInspectorOpen(true);
  };

  // Color-coded styled score badges
  const getScoreBadge = (scoreStr) => {
    const score = parseInt(scoreStr) || 0;
    if (score >= 95) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle className="w-3 h-3" />
          Excellent ({scoreStr})
        </span>
      );
    } else if (score >= 90) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          <Award className="w-3 h-3" />
          Good ({scoreStr})
        </span>
      );
    } else if (score >= 80) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <Layers className="w-3 h-3" />
          Fair ({scoreStr})
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
          <AlertTriangle className="w-3 h-3" />
          Poor ({scoreStr})
        </span>
      );
    }
  };

  // Reports list from API response
  const reports = data?.content || [];

  // Stats Counters
  const totalCount = reports.length;
  const avgScore = totalCount > 0
    ? Math.round(reports.reduce((acc, curr) => acc + (parseInt(curr.score) || 0), 0) / totalCount)
    : 0;
  const excellentCount = reports.filter((item) => (parseInt(item.score) || 0) >= 95).length;
  const reviewCount = reports.filter((item) => (parseInt(item.score) || 0) < 90).length;

  // Client-side score filter (applied on top of server-paginated data)
  const filteredReports = reports.filter((item) => {
    if (scoreFilter === "all") return true;
    const scoreVal = parseInt(item.score) || 0;
    if (scoreFilter === "excellent") return scoreVal >= 95;
    if (scoreFilter === "good") return scoreVal >= 90 && scoreVal < 95;
    if (scoreFilter === "fair") return scoreVal < 90;
    return true;
  });

  return (
    <div className="grid gap-6 p-1 md:p-6">
      {/* Header section with high-end border accent */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-100 pb-5">
        <div>
          <h3 className="text-xl tab:text-2xl font-bold">
           Reports
          </h3>

        </div>
        {/* <button
          onClick={getReportsList}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 self-start md:self-auto px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh Data
        </button> */}
      </div>

      {/* Metrics Cards Grid */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 flex items-center justify-between hover:shadow-md transition-all duration-300 border-l-4 border-l-slate-800">
          <div className="grid gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Total Reports</span>
            <span className="text-3xl font-black text-slate-900">{loading ? "..." : totalCount}</span>
          </div>
          <div className="p-3 bg-slate-50 rounded-xl text-slate-800">
            <FileText className="w-6 h-6" />
          </div>
        </Card>

        <Card className="p-5 flex items-center justify-between hover:shadow-md transition-all duration-300 border-l-4 border-l-emerald-500">
          <div className="grid gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Avg Trust Score</span>
            <span className="text-3xl font-black text-slate-900">{loading ? "..." : `${avgScore}%`}</span>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
            <Activity className="w-6 h-6" />
          </div>
        </Card>

        <Card className="p-5 flex items-center justify-between hover:shadow-md transition-all duration-300 border-l-4 border-l-blue-500">
          <div className="grid gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Excellent Grade</span>
            <span className="text-3xl font-black text-slate-900">{loading ? "..." : excellentCount}</span>
          </div>
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
            <CheckCircle className="w-6 h-6" />
          </div>
        </Card>

        <Card className="p-5 flex items-center justify-between hover:shadow-md transition-all duration-300 border-l-4 border-l-amber-500">
          <div className="grid gap-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Needs Review</span>
            <span className="text-3xl font-black text-slate-900">{loading ? "..." : reviewCount}</span>
          </div>
          <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
            <AlertTriangle className="w-6 h-6" />
          </div>
        </Card>
      </div> */}

      {/* Main Table Card */}
      <Card className="p-6 grid gap-6 shadow-sm border border-slate-100">
        {/* Filter controls */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
          {/* Search box */}
          <div className="relative w-full lg:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <CommonTextField
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search reports by Brand, Model..."
              className="pl-10 w-full rounded-lg"
            />
          </div>

          {/* Quick Filters */}
          {/* <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-slate-400 uppercase mr-1">Grade:</span>
            {[
              { id: "all", label: "All Grades" },
              { id: "excellent", label: "Excellent (95+)" },
              { id: "good", label: "Good (90-94)" },
              { id: "fair", label: "Fair (<90)" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setScoreFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  scoreFilter === tab.id
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div> */}
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto rounded-lg border border-slate-100">
          <Table className="whitespace-nowrap">
            <TableHeader className="bg-slate-50/75">
              <TableRow>
                <TableHead className="w-20 font-bold text-slate-700">Report ID</TableHead>
                <TableHead className="font-bold text-slate-700">Watch Brand</TableHead>
                <TableHead className="font-bold text-slate-700">Model Name</TableHead>
                <TableHead className="font-bold text-slate-700">User ID</TableHead>
                <TableHead className="font-bold text-slate-700 text-center">Score</TableHead>
                <TableHead className="font-bold text-slate-700">Date Created</TableHead>
                <TableHead className="w-32 font-bold text-slate-700 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx} className="h-16 animate-pulse">
                    <TableCell colSpan={7}>
                      <div className="h-5 bg-slate-100 rounded w-11/12 mx-auto"></div>
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredReports.length > 0 ? (
                filteredReports.map((report) => (
                  <TableRow key={report.reportId} className="hover:bg-slate-50/75 transition-colors">
                    {/* ID */}
                    <TableCell className="font-semibold text-slate-950">
                      #{report.reportId}
                    </TableCell>

                    {/* Brand */}
                    <TableCell className="font-semibold text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        <Watch className="w-3.5 h-3.5 text-slate-500" />
                        {report.brand || "-"}
                      </span>
                    </TableCell>

                    {/* Model */}
                    <TableCell className="text-slate-600 font-medium">
                      {report.modelName || "-"}
                    </TableCell>

                    {/* User ID with Copy Action */}
                    <TableCell className="font-mono text-xs text-slate-500">
                      <div className="flex items-center gap-2 group">
                        <span className="truncate max-w-[120px]" title={report.userId}>
                          {report.userId || "-"}
                        </span>
                        {report.userId && (
                          <button
                            onClick={() => handleCopyToClipboard(report.userId, "User ID")}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-all"
                            title="Copy User ID"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </TableCell>

                    {/* Trust Score */}
                    <TableCell className="text-center">
                      {getScoreBadge(report.score)}
                    </TableCell>

                    {/* Creation Date */}
                    <TableCell className="text-slate-500 text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {report.createdAt
                          ? moment.unix(report.createdAt).format("DD/MM/YYYY hh:mm A")
                          : "-"}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-right">
                      <button
                        onClick={() => openInspector(report)}
                        className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg hover:bg-slate-900 hover:text-white transition-all shadow-sm"
                      >
                        Inspect Report
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="h-48">
                  <TableCell colSpan={7} className="text-center text-slate-400 p-8">
                    <Watch className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p className="text-base font-semibold text-slate-500">No Reports Found</p>
                    <p className="text-xs text-slate-400 mt-1">Try adjusting your filters or search keywords.</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination bar */}
        {!loading && reports.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-4 mt-2">
            <CommonPagination
              currentPage={(data?.number || 0) + 1}
              totalPages={data?.totalPages}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={size}
              onPageSizeChange={handleDataSize}
            />
          </div>
        )}
      </Card>

      {/* Inspector Details Dialog Modal */}
      <Dialog open={isInspectorOpen} onOpenChange={setIsInspectorOpen}>
        <DialogContent className="max-w-4xl p-4 sm:p-6 md:p-8 rounded-xl border border-slate-100 bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
          {/* Close Icon */}
          <button
            onClick={() => setIsInspectorOpen(false)}
            className="absolute top-3 right-3 z-10 p-1.5 rounded-lg text-slate-500 hover:text-slate-900 transition-all"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedReport && (
            <>
              {/* Dialog Header */}
              <DialogHeader className="border-b border-slate-100 pb-3 mb-3 sm:pb-4 sm:mb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 pr-8">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 px-2 py-1 rounded">
                      Report #{selectedReport.reportId}
                    </span>
                    <DialogTitle className="text-lg sm:text-2xl font-black text-slate-900 mt-1 flex items-center gap-2">
                      <Watch className="w-5 h-5 sm:w-6 sm:h-6 text-slate-800" />
                      {selectedReport.brand} {selectedReport.modelName}
                    </DialogTitle>
                  </div>
                  <div className="self-start sm:self-auto flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 uppercase">Trust Score:</span>
                    <span className="inline-flex items-center justify-center font-black text-base sm:text-lg bg-slate-900 text-white rounded-lg px-2.5 py-1 sm:px-3 sm:py-1.5">
                      {selectedReport.score}
                    </span>
                  </div>
                </div>
                <DialogDescription className="sr-only">
                  Watch Authentication details for report #{selectedReport.reportId}
                </DialogDescription>
              </DialogHeader>

              {/* Responsive Layout Split */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 items-start">
                
                {/* Left side: interactive photo gallery viewer */}
                <div className="md:col-span-6 grid gap-3 sm:gap-4">
                  {/* Big Image display */}
                  <div className="aspect-[4/3] rounded-xl bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shadow-inner relative group">
                    <WatchImage
                      src={selectedReport[activeImageKey]}
                      alt={`${activeImageKey} view`}
                      className="w-full h-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-slate-950/85 backdrop-blur-md text-white text-[9px] sm:text-[10px] font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-md uppercase tracking-wider shadow-sm">
                      {activeImageKey.replace("Image", "")} View
                    </div>
                  </div>

                  {/* Thumbnail Selector Grid */}
                  <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                    {[
                      { key: "frontImage", label: "Front" },
                      { key: "backImage", label: "Back" },
                      { key: "crownImage", label: "Crown" },
                      { key: "strapImage", label: "Strap" },
                      { key: "claspImage", label: "Clasp" },
                    ].map((item) => (
                      <button
                        key={item.key}
                        onClick={() => setActiveImageKey(item.key)}
                        className={`aspect-square rounded-lg border-2 overflow-hidden bg-slate-50 transition-all ${
                          activeImageKey === item.key
                            ? "border-slate-900 shadow-md ring-2 ring-slate-900/10"
                            : "border-slate-200 hover:border-slate-400"
                        }`}
                        title={`${item.label} view`}
                      >
                        <WatchImage
                          src={selectedReport[item.key]}
                          alt={item.label}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Right side: comprehensive metadata sheet */}
                <div className="md:col-span-6 grid gap-4 sm:gap-6">
                  {/* Watch Details */}
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 sm:mb-3">Watch Authentication Details</h3>
                    <div className="grid grid-cols-2 gap-y-3 sm:gap-y-4 gap-x-2 text-sm">
                      <div>
                        <span className="block text-[10px] font-semibold text-slate-400 uppercase">Brand</span>
                        <span className="font-bold text-slate-800">{selectedReport.brand || "Fastrack"}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-semibold text-slate-400 uppercase">Model Name</span>
                        <span className="font-bold text-slate-800">{selectedReport.modelName || "-"}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-semibold text-slate-400 uppercase">Reference No.</span>
                        <span className="font-semibold text-slate-600 font-mono text-xs truncate max-w-[150px]">
                          {selectedReport.referenceNumber || "Not Provided"}
                        </span>
                      </div>
                      <div>
                        <span className="block text-[10px] font-semibold text-slate-400 uppercase">Serial No.</span>
                        <span className="font-semibold text-slate-600 font-mono text-xs truncate max-w-[150px]">
                          {selectedReport.serialNumber || "Not Provided"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Users Metadata */}
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-slate-100">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 sm:mb-3">Metadata</h3>
                    <div className="grid gap-2 sm:gap-3 text-sm">
                      <div>
                        <span className="block text-[10px] font-semibold text-slate-400 uppercase">User Identification</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="font-mono text-xs text-slate-600 truncate max-w-[180px] sm:max-w-[220px]" title={selectedReport.userId}>
                            {selectedReport.userId}
                          </span>
                          <button
                            onClick={() => handleCopyToClipboard(selectedReport.userId, "User ID")}
                            className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors"
                            title="Copy User ID"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 border-t border-slate-200/50 pt-2 mt-1">
                        <div>
                          <span className="block text-[10px] font-semibold text-slate-400 uppercase">Issued Date</span>
                          <span className="font-medium text-slate-700 text-xs mt-0.5 block">
                            {selectedReport.createdAt
                              ? moment.unix(selectedReport.createdAt).format("LL")
                              : "-"}
                          </span>
                        </div>
                        <div>
                          <span className="block text-[10px] font-semibold text-slate-400 uppercase">Issued Time</span>
                          <span className="font-medium text-slate-700 text-xs mt-0.5 block">
                            {selectedReport.createdAt
                              ? moment.unix(selectedReport.createdAt).format("LT")
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions Section */}
                  <div className="grid gap-2">
                    {selectedReport.reportPdf && (
                      <a
                        href={selectedReport.reportPdf}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 sm:py-3 text-sm font-bold text-white bg-slate-900 rounded-xl hover:bg-slate-800 hover:shadow transition-all duration-300 cursor-pointer"
                      >
                        <Download className="w-4 h-4" />
                        Download Certificate PDF
                        <ExternalLink className="w-3.5 h-3.5 ml-1 opacity-70" />
                      </a>
                    )}
                    <button
                      onClick={() => setIsInspectorOpen(false)}
                      className="inline-flex items-center justify-center w-full px-4 py-2.5 sm:py-3 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all duration-300"
                    >
                      Close 
                    </button>
                  </div>
                </div>

              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Report;